import React, { forwardRef, memo, useMemo } from "react";
import { Box } from "@mui/material";

import {
  channelToId,
  useListContainerHeight,
  VirtualizedChatList,
  VirtualizedChatListHandle,
  CLOSED_CHATS_HINT_ID,
  CLOSED_CHATS_HINT_ROW,
  CLOSED_CHATS_HINT_ROW_HEIGHT,
} from "./chatVirtualizedTree";
import { IChannel } from "../../../types/chat";
import { DEFAULT_OTHER_USER_CHAT_ROW_HEIGHT } from "../../../types/sidebar";
import { NoChatsContentMessage } from "./NoChatsContentMessage";
import { LoadingChatsMessage } from "./LoadingChatsMessage";

interface SharedChatsContentProps {
  sharedChats: IChannel[];
  chatNotifications: Set<string>;
  onChatDeleted: (chatName: string) => void;
  onChatPinned: (chatName: string, isPinned: boolean) => void;
  onChatSelected: (chatId: string, claimId: string | undefined) => void;
  filterByClaimId?: string | null;
  loading?: boolean;
  /** Show a hint after the last chat pointing users to the filters panel,
   * since closed chats are hidden from the list whenever this is true. */
  showClosedChatsHint?: boolean;
}

const SharedChatsContent = forwardRef<
  VirtualizedChatListHandle,
  SharedChatsContentProps
>(function SharedChatsContent(
  {
    sharedChats,
    chatNotifications,
    onChatDeleted,
    onChatPinned,
    onChatSelected,
    filterByClaimId,
    loading,
    showClosedChatsHint,
  },
  ref,
) {
  const { containerRef, height } = useListContainerHeight();

  const chatsWithHint = useMemo(
    () =>
      showClosedChatsHint && sharedChats.length > 0
        ? [...sharedChats, CLOSED_CHATS_HINT_ROW]
        : sharedChats,
    [sharedChats, showClosedChatsHint],
  );

  // Mirrors VirtualizedChatList's own item-count fallback, so the list can
  // render at (roughly) the right height before the container is measured.
  const fallbackHeight = useMemo(
    () =>
      chatsWithHint.reduce(
        (total, chat) =>
          total +
          (channelToId(chat) === CLOSED_CHATS_HINT_ID
            ? CLOSED_CHATS_HINT_ROW_HEIGHT
            : DEFAULT_OTHER_USER_CHAT_ROW_HEIGHT),
        0,
      ),
    [chatsWithHint],
  );

  const claimDividerChatId = useMemo(() => {
    if (!filterByClaimId) {
      return undefined;
    }

    for (let i = sharedChats.length - 1; i >= 0; i -= 1) {
      if (sharedChats[i].claim_number === filterByClaimId) {
        return channelToId(sharedChats[i]);
      }
    }

    return undefined;
  }, [sharedChats, filterByClaimId]);

  if (loading) {
    return <LoadingChatsMessage />;
  }

  if (sharedChats.length === 0) {
    return (
      <Box sx={{ px: 1 }}>
        <NoChatsContentMessage
          title="No shared chats yet"
          subtitle="Chats shared with you will appear here."
          showClosedChatsHint={showClosedChatsHint}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        px: 1,
        flexDirection: "column",
        gap: 0.5,
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <VirtualizedChatList
          ref={ref}
          chats={chatsWithHint}
          chatNotifications={chatNotifications}
          height={height <= 0 ? fallbackHeight : height}
          rowHeight={DEFAULT_OTHER_USER_CHAT_ROW_HEIGHT}
          onChatDeleted={onChatDeleted}
          onChatPinned={onChatPinned}
          onChatSelected={onChatSelected}
          dividerAfterChatId={claimDividerChatId}
        />
      </Box>
    </Box>
  );
});

export default memo(SharedChatsContent);
